import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  AreaChart, Area, CartesianGrid, LabelList,
} from 'recharts';
import AppLayout from '../../components/AppLayout';
import useClaimsAnalyticsFunctions, { DATE_PRESETS, TABS } from './useClaimsAnalyticsFunctions';
import styles from './ClaimsAnalytics.module.css';
import { formatCurrency } from '../regureStyles';
import { useLocale } from '../../utils/LocaleContext';

const BURGUNDY = '#800020';
const GOLD     = '#c9a02b';
const CHARCOAL = '#1e293b';

// Backend emits raw LOB codes on row.product; frontend maps to the same
// screens.claimsScreen.lob* keys used by ClaimsScreen so labels match.
const LOB_LABEL_KEY = {
  daily_sickness:         'screens.claimsScreen.lobDailySickness',
  professional_liability: 'screens.claimsScreen.lobProfessionalLiability',
  legal_protection:       'screens.claimsScreen.lobLegalProtection',
  surplus_lines:          'screens.claimsScreen.lobSurplusLines',
  property:               'screens.claimsScreen.lobProperty',
  casualty:               'screens.claimsScreen.lobCasualty',
  commercial:             'screens.claimsScreen.lobCommercial',
  auto:                   'screens.claimsScreen.lobAuto',
  health:                 'screens.claimsScreen.lobHealth',
  workers_comp:           'screens.claimsScreen.lobWorkersComp',
};

function translateDimension(row, tabId, t) {
  if (tabId === 'product') {
    const key = LOB_LABEL_KEY[row.product];
    if (key) return t(key);
    return row.productLabel || row.product || '—';
  }
  const dimKey = TABS.find(x => x.id === tabId)?.dimensionKey;
  const raw = dimKey ? row[dimKey] : null;
  if (raw === 'Unknown')     return t('claimsAnalytics.unknown');
  if (raw === 'Unspecified') return t('claimsAnalytics.unspecified');
  // A policy with no broker attached. The backend now returns null here
  // rather than the English literal "No Broker" it used to write into the
  // data — the label is the tenant's own noun, interpolated from the same
  // vocabulary that renames the column header above it. MOK reads
  // "No Sponsor". The legacy literal is still recognised so a cached or
  // older response degrades to the translated label instead of leaking.
  if (dimKey === 'broker' && (raw == null || raw === 'No Broker')) {
    return t('claimsAnalytics.noBroker');
  }
  return raw || '—';
}

// Average duration is only defined over claims that actually closed. The
// backend returns null when no claim in the bucket has both a reported and
// a closed date; "0 days" would be a measurement nobody made.
function durationLabel(days, t, unitKey) {
  if (days == null) return t('claimsAnalytics.notAvailable');
  return `${days} ${t(unitKey)}`;
}

// Use the tenant-wide currency helper so the Analytics page renders the same
// symbol the rest of the app uses (driven by Account Settings locale).
const money = (n) => formatCurrency(n);

function lossRatioColor(ratio) {
  if (ratio == null) return '#94a3b8';
  if (ratio < 0.6)   return '#10b981'; // green
  if (ratio <= 0.8)  return '#f59e0b'; // amber
  return '#ef4444';                     // red
}

// The backend returns null when the premium base cannot support the ratio
// (no premium, or a denominator covering a different population than the
// numerator — see lossRatioFor). Render that as "not available" rather than
// as a dash that reads like a zero, and never as the 1943% it used to be.
function lossRatioLabel(ratio, t) {
  if (ratio == null) return t ? t('claimsAnalytics.notAvailable') : '—';
  return `${Math.round(ratio * 100)}%`;
}

const ClaimsAnalyticsScreen = () => {
  const { t } = useTranslation();
  useLocale(); // reactive — re-renders when the user changes currency
  const fns = useClaimsAnalyticsFunctions();

  // Pre-translate productBreakdown so the chart Y-axis renders localized
  // labels instead of the English strings the backend hardcodes in
  // PRODUCT_LABELS. Re-computed on every render; language change already
  // triggers a re-render via useTranslation.
  const translatedProductBreakdown = (fns.productBreakdown || []).map(b => {
    const key = LOB_LABEL_KEY[b.product];
    return { ...b, productLabel: key ? t(key) : (b.productLabel || b.product) };
  });

  return (
    <AppLayout>
      {/* AppLayout's main is overflow:hidden — the direct child must
          own its own vertical scroll or content past the viewport is
          clipped (gotcha-applayout-overflow-hidden). Anjani couldn't
          reach the tabbed table below the reserve-development chart
          because the previous single .container never scrolled. */}
      <div style={{ flex: 1, overflowY: 'auto', width: '100%' }}>
        <div className={styles.container}>
          {/* Header */}
        <div className={styles.header}>
          <div>
            <div className={styles.breadcrumb}>{t('nav.sectionIntelligence')} &nbsp;›&nbsp; {t('nav.claimsAnalytics')}</div>
            <h1 className={styles.title}>{t('nav.claimsAnalytics')}</h1>
          </div>

          <div className={styles.headerControls}>
            <select
              value={fns.preset}
              onChange={e => fns.setPreset(e.target.value)}
              className={styles.select}
            >
              {DATE_PRESETS.map(p => (
                <option key={p.id} value={p.id}>{t(p.labelKey)}</option>
              ))}
            </select>

            {fns.preset === 'custom' && (
              <div className={styles.dateRangeGroup}>
                <input type='date' value={fns.customFrom} onChange={e => fns.setCustomFrom(e.target.value)} className={styles.dateInput} />
                <span className={styles.dateRangeSeparator}>→</span>
                <input type='date' value={fns.customTo}   onChange={e => fns.setCustomTo(e.target.value)}   className={styles.dateInput} />
              </div>
            )}

            <button onClick={fns.refresh} className={styles.refreshBtn}>{t('claimsAnalytics.refresh')}</button>
          </div>
        </div>

        {fns.error && <div className={styles.errorBanner}>{fns.error}</div>}

        {/* Row 1: KPI cards */}
        <div className={styles.kpiRow}>
          <KpiCard label={t('claimsAnalytics.kpiTotalClaims')}  value={fns.summary?.totalClaims ?? '—'} loading={fns.loading} />
          <KpiCard label={t('claimsAnalytics.kpiOpenClaims')}   value={fns.summary?.openClaims ?? '—'}  loading={fns.loading} />
          <KpiCard label={t('claimsAnalytics.kpiTotalReserve')} value={fns.summary ? money(fns.summary.totalReserve) : '—'} loading={fns.loading} />
          <KpiCard label={t('claimsAnalytics.kpiTotalPaid')}    value={fns.summary ? money(fns.summary.totalPaid) : '—'} loading={fns.loading} />
          <KpiCard
            label={t('claimsAnalytics.kpiLossRatio')}
            value={fns.summary ? lossRatioLabel(fns.summary.lossRatio, t) : '—'}
            loading={fns.loading}
            valueColor={fns.summary ? lossRatioColor(fns.summary.lossRatio) : undefined}
          />
          <KpiCard label={t('claimsAnalytics.kpiAvgDuration')} value={fns.summary ? durationLabel(fns.summary.averageClaimDuration, t, 'claimsAnalytics.days') : '—'} loading={fns.loading} />
        </div>

        {/* Row 2: Charts */}
        <div className={styles.chartRow}>
          <div className={styles.chartCard}>
            <div className={styles.chartTitle}>{t('claimsAnalytics.chartByProduct')}</div>
            <div className={styles.chartBody}>
              <ResponsiveContainer width='100%' height={260}>
                <BarChart data={translatedProductBreakdown} layout='vertical' margin={{ top: 10, right: 30, left: 60, bottom: 10 }}>
                  <CartesianGrid strokeDasharray='3 3' stroke='#e2e8f0' />
                  <XAxis type='number' stroke='#64748b' fontSize={12} />
                  <YAxis type='category' dataKey='productLabel' stroke='#64748b' fontSize={12} width={150} />
                  <Tooltip
                    formatter={(value, _name, ctx) => {
                      if (ctx?.payload?.lossRatio != null) {
                        return [`${value} ${t('claimsAnalytics.claims')} • ${t('claimsAnalytics.lossRatio')} ${lossRatioLabel(ctx.payload.lossRatio, t)}`, t('claimsAnalytics.claims')];
                      }
                      // Return the NAME as well as the value. Recharts falls
                      // back to the series dataKey when a formatter returns a
                      // bare value, which put the raw field name 'claimCount'
                      // in front of the user on Cases by Product.
                      return [value, t('claimsAnalytics.colClaimCount')];
                    }}
                  />
                  {/* `name` is what Recharts labels the series with wherever a
                      formatter does not override it — legend, tooltip header,
                      accessibility layer. Without it the label is the dataKey. */}
                  <Bar dataKey='claimCount' name={t('claimsAnalytics.colClaimCount')} fill={BURGUNDY} radius={[0, 6, 6, 0]}>
                    <LabelList dataKey='lossRatio' position='right' formatter={(v) => lossRatioLabel(v, t)} fill={CHARCOAL} fontSize={11} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className={styles.chartCard}>
            <div className={styles.chartTitle}>{t('claimsAnalytics.chartReserveDev')}</div>
            <div className={styles.chartBody}>
              <ResponsiveContainer width='100%' height={260}>
                <AreaChart data={fns.reserveDevelopment} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                  <defs>
                    <linearGradient id='reserveGradient' x1='0' y1='0' x2='0' y2='1'>
                      <stop offset='0%' stopColor={BURGUNDY} stopOpacity={0.35} />
                      <stop offset='100%' stopColor={BURGUNDY} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray='3 3' stroke='#e2e8f0' />
                  <XAxis dataKey='month' stroke='#64748b' fontSize={11} />
                  <YAxis stroke='#64748b' fontSize={11} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={v => money(v)} />
                  <Area type='monotone' dataKey='closingReserve' stroke={BURGUNDY} fill='url(#reserveGradient)' strokeWidth={2.2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Row 3: Tabbed tables */}
        <div className={styles.tableCard}>
          <div className={styles.tabBar}>
            {/* Until the breakdowns come back we do not know which
                dimensions this tenant has, so the bar holds neutral,
                unlabelled placeholders rather than naming the full built-in
                list. Two chips is a layout hint, not a count. */}
            {fns.loading
              ? [0, 1].map(i => <div key={i} className={styles.tabSkeleton} aria-hidden='true' />)
              : (fns.visibleTabs || TABS).map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => fns.setActiveTab(tab.id)}
                    className={`${styles.tab} ${fns.activeTab === tab.id ? styles.tabActive : ''}`}
                  >
                    {t(tab.labelKey)}
                  </button>
                ))}
            <div style={{ flex: 1 }} />
            {/* Nothing to export from a breakdown whose every row is a
                placeholder — the table is not showing those rows either. */}
            <button
              onClick={fns.exportCsv}
              className={styles.exportBtn}
              disabled={!fns.sortedTableRows.length || fns.activeTabHasSignal === false}
            >
              {t('claimsAnalytics.exportCsv')}
            </button>
          </div>

          <AnalyticsTable
            t={t}
            activeTab={fns.activeTab}
            rows={fns.sortedTableRows}
            sortBy={fns.sortBy}
            onSort={fns.handleSort}
            loading={fns.loading}
            hasSignal={fns.activeTabHasSignal}
          />
        </div>
        </div>
      </div>
    </AppLayout>
  );
};

const KpiCard = ({ label, value, loading, valueColor }) => (
  <div className={styles.kpiCard}>
    <div className={styles.kpiLabel}>{label}</div>
    <div className={styles.kpiValue} style={valueColor ? { color: valueColor } : undefined}>
      {loading ? <span className={styles.skeleton} /> : value}
    </div>
  </div>
);

const AnalyticsTable = ({ t, activeTab, rows, sortBy, onSort, loading, hasSignal = true }) => {
  const tab = TABS.find(x => x.id === activeTab);
  const dimensionKey = tab.dimensionKey;
  const dimensionLabelKey = tab.labelKey;

  const columns = [
    // The first column names the dimension, and while loading that name is
    // still whatever tab happened to be selected — so the header announced
    // "BY AGE GROUP" over a spinner on a tenant that has no such breakdown.
    // The measure columns are the same on every dimension and can stay.
    { key: dimensionKey,    label: loading ? '' : t(dimensionLabelKey),     align: 'left' },
    { key: 'claimCount',    label: t('claimsAnalytics.colClaimCount'),      align: 'right' },
    { key: 'totalReserve',  label: t('claimsAnalytics.colTotalReserve'),    align: 'right' },
    { key: 'totalPaid',     label: t('claimsAnalytics.colTotalPaid'),       align: 'right' },
    { key: 'lossRatio',     label: t('claimsAnalytics.colLossRatio'),       align: 'right' },
    { key: 'averageDuration', label: t('claimsAnalytics.colAvgDuration'),   align: 'right' },
  ];

  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            {columns.map(col => (
              <th
                key={col.key}
                onClick={() => onSort(col.key)}
                style={{ textAlign: col.align, cursor: 'pointer' }}
              >
                {col.label}
                {sortBy.column === col.key && <span className={styles.sortArrow}>{sortBy.direction === 'desc' ? ' ▼' : ' ▲'}</span>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading && rows.length === 0 && (
            <tr><td colSpan={columns.length} className={styles.tableEmpty}>{t('claimsAnalytics.loading')}</td></tr>
          )}
          {!loading && rows.length === 0 && (
            <tr><td colSpan={columns.length} className={styles.tableEmpty}>{t('claimsAnalytics.empty')}</td></tr>
          )}
          {/* Rows exist, but every one of them is a placeholder. Say so
              instead of presenting an unfilled field as a breakdown. */}
          {!loading && rows.length > 0 && !hasSignal && (
            <tr><td colSpan={columns.length} className={styles.tableEmpty}>{t('claimsAnalytics.dimensionUnavailable')}</td></tr>
          )}
          {hasSignal && rows.map((row, i) => (
            <tr key={i}>
              <td style={{ textAlign: 'left' }}>{translateDimension(row, activeTab, t)}</td>
              <td style={{ textAlign: 'right' }}>{row.claimCount}</td>
              <td style={{ textAlign: 'right' }}>{money(row.totalReserve)}</td>
              <td style={{ textAlign: 'right' }}>{money(row.totalPaid)}</td>
              <td style={{ textAlign: 'right', color: lossRatioColor(row.lossRatio), fontWeight: 600 }}>{lossRatioLabel(row.lossRatio, t)}</td>
              <td style={{ textAlign: 'right' }}>{durationLabel(row.averageDuration, t, 'claimsAnalytics.daysShort')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ClaimsAnalyticsScreen;
